/* eslint-disable max-lines */
/* eslint-disable react/jsx-handler-names */
import PropTypes from "prop-types";
import dynamic from "next/dynamic";
import SearchBar from "../header/searchBar";
import Drawer from "../../molecules/drawer";
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchSearchAutoSuggestions, fetchSearchSuggestions } from "../../../api/common";
import { fetchDefaultSearchPageFeed } from "../../../utils/dynamicHomeUtils/defaultSearchFeed";
import { debounce, pageName, toggleElementVisibility } from "../../../utils/common";
import { getProductListingDetailsAPI } from "../../../api/plpApi";
import CookieUtil from "../../../utils/storage/cookieUtil";
import { plpPageConstants } from "../../../constants/pageConstants/plpConstants";
import {
  COUNTRIES,
  DEBOUNCE_TIME,
  MAX_COUNT_RECENT_SEARCH,
  RECENT_SEARCH_LIST,
  TYPE_TEXT_AND_HIT_ENTER,
} from "../../../constants/common/common";
import { getLocalStorageItem, setLocalStorageItem } from "../../../utils/storage/localStorageUtil";
import useAnalytics from "../../../analytics/useAnalytics";
import { cleverTapCommonEvents } from "../../../analytics/clevertapConstants";
import { GA4_EVENTS } from "../../../analytics/gaConstant";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { setIsMobileSearchVisibleAction, setIsNavigatingAction } from "../../../store/slices/commonSlice";
import DefaultSearchPage from "../defaultSearchPage/DefaultSearchPage";
import { useABTestingView } from "../../../customHooks/clevertap/useABTestingView";
import Suggestions from "./searchComponents/suggestions";
import IconButtonComponent from "../../atoms/iconButton";
import Typography from "../../atoms/typography";
import OldRecentSearch from "./searchComponents/oldRecentSearch";
import { usePathname } from "next/navigation";
import { lockBody, unlockBody } from "../../../utils/document";
const AutoSuggestions = dynamic(() => import("../desktopSearch/autoSuggestions"), { ssr: false });
const VoiceSearch = dynamic(() => import("./searchComponents/voiceSearch"), { ssr: false });
const AiGiftFinderIsland = dynamic(() => import("../../../containers/aiGiftFinder/views/AiGiftFinderIsland"), {
  ssr: false,
});

/**
 * MobileSearch component handles search functionality for mobile devices,
 * including recent searches, search suggestions, and voice search.
 *
 * @param {Object} props - Component properties.
 * @param {Function} [props.setShowSearchPopUp=()=>{}] - Function to control the visibility of the search popup.
 *
 * @returns {JSX.Element} The rendered MobileSearch component.
 */
const MobileSearch = ({
  setShowSearchPopUp = () => {},
  catalogId,
  isSearchIconVisible = false,
  searchBarStyles = {},
  inputClasses = "",
  isGiftFinderEnabled = true,
}) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const [recentSearchCount, setRecentSearchCount] = useState(0);
  const [isShowVoiceSearchDrawer, setIsShowVoiceSearchDrawer] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [searchSuggestion, setSearchSuggestion] = useState(null);
  const [recentSearch, setRecentSearch] = useState([]);
  const [autoFocus, setAutoFocus] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [defaultSearchWidgets, setDefaultSearchWidgets] = useState([]);
  const [isDefaultLoading, setIsDefaultLoading] = useState(false);
  const inputRef = useRef(null);
  const pathname = usePathname();
  const completePathName = typeof window !== "undefined" && window.location.href ? window.location.href : null;
  const isHome = pathname === "/";
  const isPLP = pathname?.includes("-lp");

  const { autoSuggestion, recallABTestingHook } = useABTestingView();

  const {
    isMobileSearchDrawerVisible,
    pincode,
    domesticAddress,
    selectedIntLocation,
    loginDetail,
    product,
    pincodeIntl,
  } = useSelector(
    (state) => ({
      isMobileSearchDrawerVisible: state?.common?.isMobileSearchDrawerVisible,
      pincode: state?.locationLock?.selectedDomesticLocation?.pincode,
      domesticAddress: state?.locationLock?.selectedDomesticLocation?.address,
      selectedIntLocation: state?.locationLock?.selectedIntLocation?.location,
      loginDetail: state?.fus?.loginDetail,
      product: state?.pdp?.clientCurrentClickedItem,
      pincodeIntl: state?.locationLock?.selectedIntLocation?.pincode,
    }),
    shallowEqual,
  );
  const urlIdentifier = product?.urlIdentifier;
  const updatedPincode = catalogId !== COUNTRIES.INDIA ? pincodeIntl : pincode;

  useEffect(() => {
    recallABTestingHook?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginDetail?.loggedin, isMobileSearchDrawerVisible]);

  const searchEventCommonData = {
    country: catalogId,
    city: catalogId !== COUNTRIES.INDIA ? selectedIntLocation : "",
    area: catalogId !== COUNTRIES.INDIA ? "" : domesticAddress || "",
    pincode: catalogId !== COUNTRIES.INDIA ? "" : pincode || "",
    system_pincode: catalogId !== COUNTRIES.INDIA ? "" : pincode || "",
  };

  // Load recent searches from localStorage on mount
  useEffect(() => {
    const searchHistory = getLocalStorageItem(RECENT_SEARCH_LIST);
    const searchHistoryRecentSearchList = searchHistory && JSON.parse(searchHistory);
    searchHistory && setRecentSearch(searchHistoryRecentSearchList?.recentSearchList);
  }, []);

  // Debounced version of the search function to fetch suggestions
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchSuggestions = useCallback(
    debounce(async (value) => {
      if (value.trim().length <= 2) {
        setIsLoading(false);
        return;
      }
      if (autoSuggestion) {
        try {
          setIsLoading(true);
          const response = await fetchSearchAutoSuggestions(catalogId, value, updatedPincode);
          setSearchSuggestion(response);
          setRecentSearchCount(response?.recentSearchCount);
        } catch (error) {
          console.error("Error fetching search suggestions:", error);
          setSearchSuggestion(null);
        } finally {
          setIsLoading(false);
        }
      } else {
        const response = await fetchSearchSuggestions(catalogId, value, 1234);
        response?.suggestionResponse && setSearchSuggestion(response?.suggestionResponse);
        response?.recentSearchCount && setRecentSearchCount(response?.recentSearchCount);
      }
    }, DEBOUNCE_TIME),
    [autoSuggestion, updatedPincode],
  );

  /** */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchDefaultSuggestions = useCallback(async (activeCatalogId) => {
    try {
      const widgets = await fetchDefaultSearchPageFeed({
        isMobile: true,
        catalogId: activeCatalogId,
      });
      setDefaultSearchWidgets(widgets);
    } catch (error) {
      console.error("Error fetching default search feed:", error);
      setDefaultSearchWidgets([]);
    } finally {
      setIsDefaultLoading(false);
    }
  }, []);

  /**
   * Handles search input changes and triggers the suggestion fetch.
   *
   * @param {Event} event - The input change event.
   */
  const onSearch = async (event) => {
    const value = event.target.value;
    setInputValue(value);
    if (value.length > 0) {
      debouncedFetchSuggestions(value);
    } else {
      setIsLoading(false);
      setSearchSuggestion(null);
    }
  };

  /**
   * Clears the current search input and suggestions.
   */
  const onClearSearch = () => {
    setInputValue("");
    setSearchSuggestion(null);
    setIsLoading(false);
  };

  /**
   * Adds a search keyword to the recent search list in localSt orage.
   *
   * @param {string} keyword - The search keyword.
   * @param {string} url - The URL associated with the search keyword.
   */
  const addRecentSearchKeyword = (keyword, url) => {
    const searchHistory = getLocalStorageItem(RECENT_SEARCH_LIST);
    let recentSearchList = searchHistory ? JSON.parse(searchHistory) : { recentSearchList: [] };
    // Remove the existing object with the same keyword and URL, if it exists
    recentSearchList.recentSearchList = recentSearchList.recentSearchList.filter(
      (item) => item.keyword !== keyword || item.url !== url,
    );
    // Check if the list exceeds 8 items, remove the oldest one
    if (recentSearchList.recentSearchList.length >= MAX_COUNT_RECENT_SEARCH) {
      recentSearchList?.recentSearchList?.pop();
    }
    // Add the new search entry
    recentSearchList?.recentSearchList?.unshift({
      keyword: keyword,
      url: url,
      suggestionResponseType: "RS",
    });
    // Update localStorage
    setLocalStorageItem(RECENT_SEARCH_LIST, JSON.stringify(recentSearchList));
  };

  /**
   * Closes the mobile search drawer for navigation
   */
  const closeSearchDrawer = () => {
    dispatch(setIsMobileSearchVisibleAction(false));
  };

  /**
   * Closes drawer and shows PLP skeleton for category/search navigation (not for product clicks)
   */
  const onCategoryOrSearchNavigate = () => {
    dispatch(setIsNavigatingAction(true));
    closeSearchDrawer();
  };

  /**
   * Handles the form submission for searching and redirects to the product listing page.
   *
   * @param {Event} e - The form submit event.
   */
  const onSubmitForm = async (e, value) => {
    if (!value || value?.trim()?.length === 0) {
      e.preventDefault();
      return;
    }
    if (e) e.preventDefault();
    onCategoryOrSearchNavigate();
    try {
      const pageListingData = {
        page: plpPageConstants.INITIAL_PAGE,
        size: plpPageConstants.DESKTOP_PRODUCT_PER_PAGE,
        geoId: catalogId,
        isFromApp: "yes",
        pageType: "search",
        qs: value,
        fnpId: CookieUtil.getCookie("ab_user_id"),
      };
      const response = await getProductListingDetailsAPI(pageListingData);

      // Track events before navigation
      const clevertapSearchData = {
        ...searchEventCommonData,
        search: value,
        is_popular_search: false,
        is_recent_search: false,
        is_manual_boost: false,
      };
      trackEvent(cleverTapCommonEvents.searched, clevertapSearchData, ["clevertap"]);
      trackEvent(GA4_EVENTS?.SEARCH, { search: value || null }, ["ga4"]);

      // Use requestAnimationFrame to ensure state update renders before navigation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (response?.redirectUrl) {
            const url = new URL(response.redirectUrl);
            const relativePath = `${url?.pathname}${url?.search}`;
            addRecentSearchKeyword(value, relativePath);
            window.location.href = relativePath;
          } else {
            const searchUrl = `/search?FNP_CURRENT_CATALOG_ID=${catalogId}&qs=${value}`;
            addRecentSearchKeyword(value, searchUrl);
            window.location.href = searchUrl;
          }
        });
      });
    } catch (error) {
      console.error("Error during search:", error);
    }
  };

  /**
   * Closes the search popup and resets its state.
   */
  const onCloseSearch = () => {
    setShowSearchPopUp(false);
    setInputValue("");
    dispatch(setIsMobileSearchVisibleAction(false));
  };

  /**
   * Toggles the visibility of the voice search drawer.
   */
  const onCloseVoiceSearch = () => {
    setInputValue("");
    toggleElementVisibility("sticky_bottom_menu", true);
    setIsShowVoiceSearchDrawer(!isShowVoiceSearchDrawer);
  };

  /**
   * Tracks a search event with Analytics.
   *
   * @param {string} value - The search item object.
   */
  const onSearchTrackEvent = (value, item) => {
    const clevertapSearchData = {
      ...searchEventCommonData,
      search: item?.categoryName || item?.keyword,
      is_popular_search: item?.suggestionResponseType === "PR",
      is_recent_search: item?.suggestionResponseType === "RS",
      is_manual_boost: item?.suggestionResponseType === "MB",
    };
    trackEvent(cleverTapCommonEvents.searched, clevertapSearchData, ["clevertap"]);
    trackEvent(GA4_EVENTS?.SEARCH, { search: value || null }, ["ga4"]);
  };

  /**
   * handleMicClick handle mic click
   */
  const handleMicClick = (handleSearchPopup) => {
    setAutoFocus(false);
    setIsShowVoiceSearchDrawer(true);
    handleSearchPopup && dispatch(setIsMobileSearchVisibleAction(true));
    toggleElementVisibility("sticky_bottom_menu", false);
  };

  /** */
  const onSearchBarClick = () => {
    setIsDefaultLoading(true);
    dispatch(setIsMobileSearchVisibleAction(true));
    setAutoFocus(true);
    const searchSelectedData = {
      page_name: pageName(isHome, isPLP, pathname, urlIdentifier),
      pageType: (typeof window !== "undefined" && window.fnpPageType) || "",
      complete_url: completePathName,
      clean_url: pathname,
    };
    trackEvent(cleverTapCommonEvents.searchSelected, searchSelectedData, ["clevertap"]);
  };

  /**
   * Handles touch outside search bar to remove focus
   */
  const handleTouchOutside = (event) => {
    const searchBar = document.getElementById("search_bar");
    if (searchBar && !searchBar.contains(event.target) && inputRef.current) {
      inputRef.current.blur();
      setAutoFocus(false);
    }
  };

  useEffect(() => {
    if (isMobileSearchDrawerVisible && autoSuggestion) {
      setIsDefaultLoading(true);
      fetchDefaultSuggestions(catalogId);
    }
  }, [autoSuggestion, catalogId, fetchDefaultSuggestions, isMobileSearchDrawerVisible]);

  // Add touch outside event listener to remove focus
  useEffect(() => {
    if (isMobileSearchDrawerVisible) {
      document.addEventListener("touchstart", handleTouchOutside);

      return () => {
        document.removeEventListener("touchstart", handleTouchOutside);
      };
    }
  }, [isMobileSearchDrawerVisible]);

  useEffect(() => {
    if (!isMobileSearchDrawerVisible) {
      return undefined;
    }

    lockBody();

    return () => {
      unlockBody();
    };
  }, [isMobileSearchDrawerVisible]);

  return (
    <>
      {!isSearchIconVisible && (
        <div className={`mt-4 flex w-full items-center ${isGiftFinderEnabled ? "gap-8" : ""}`} style={searchBarStyles}>
          <div className="min-w-0 flex-1">
            <SearchBar
              isShowStartAdornment={true}
              isShowEndAdornment={true}
              isReadonly={true}
              isMobile={true}
              onSearchBarClick={onSearchBarClick}
              onMicClick={() => handleMicClick(true)}
              inputClasses={inputClasses}
            />
          </div>
          {isGiftFinderEnabled && (
            <AiGiftFinderIsland
              launcherClassName="h-auto shrink-0 grow-0 basis-[20%] self-stretch"
              isMobile={true}
              catalogId={catalogId}
            />
          )}
        </div>
      )}
      {isMobileSearchDrawerVisible && (
        <div className="fixed inset-0 z-[99999] flex h-[100dvh] w-full flex-col overflow-hidden overscroll-none bg-white-900">
          {autoSuggestion ? (
            <>
              <div className="flex w-full items-center gap-8 bg-fnp-20 px-16 pb-12 pt-8">
                <form onSubmit={(e) => onSubmitForm(e, inputValue)} className="min-w-0 flex-1">
                  <SearchBar
                    isShowStartAdornment={true}
                    isShowEndAdornment={true}
                    inputValue={inputValue}
                    onClearSearch={onClearSearch}
                    onCloseSearch={onCloseSearch}
                    isFromSearch={true}
                    isAutoFocus={autoFocus}
                    onChange={onSearch}
                    isMobile={true}
                    onMicClick={() => handleMicClick(false)}
                    customRef={inputRef}
                  />
                </form>
                {isGiftFinderEnabled && (
                  <AiGiftFinderIsland launcherClassName="h-40 flex-none" isMobile={true} catalogId={catalogId} />
                )}
              </div>
              <div className="flex-1 overflow-hidden">
                {inputValue.length >= 3 ? (
                  <AutoSuggestions
                    combinedSearch={{
                      recentSearch: Array.isArray(recentSearch) ? recentSearch.slice(-recentSearchCount) : [],
                      searchSuggestion: searchSuggestion || {},
                    }}
                    searchEventCommonData={searchEventCommonData}
                    inputValue={inputValue}
                    isLoading={isLoading}
                    setRecentSearch={setRecentSearch}
                    isMobile={true}
                    onNavigate={onCategoryOrSearchNavigate}
                  />
                ) : (
                  <DefaultSearchPage
                    widgets={defaultSearchWidgets}
                    isMobile={true}
                    isFeedLoading={isDefaultLoading}
                    onNavigate={onCategoryOrSearchNavigate}
                  />
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex h-44 w-full items-center justify-start bg-fnp-100 p-16">
                <span>
                  <IconButtonComponent
                    labelColor="w-full font-normal"
                    classes="py-2"
                    iconSrc="/icons/left-arrow.svg"
                    imgHeight={24}
                    imgWidth={24}
                    onClick={onCloseSearch}
                    isPriority={false}
                  />
                </span>
              </div>
              <div className="flex items-center gap-8 p-16">
                <form onSubmit={(e) => onSubmitForm(e, inputValue)} className="min-w-0 flex-1">
                  <SearchBar
                    isShowStartAdornment={true}
                    isShowEndAdornment={true}
                    inputValue={inputValue}
                    onClearSearch={onClearSearch}
                    isAutoFocus={autoFocus}
                    onChange={onSearch}
                    isMobile={true}
                    onMicClick={() => handleMicClick(false)}
                  />
                </form>
                {isGiftFinderEnabled && (
                  <AiGiftFinderIsland launcherClassName="h-40 flex-none" isMobile={true} catalogId={catalogId} />
                )}
              </div>
              <Typography variant="p" Tag="p" classes="px-16 font-400 text-14 text-grey-500">
                {TYPE_TEXT_AND_HIT_ENTER}
              </Typography>
              {searchSuggestion && searchSuggestion.length > 0 && (
                <>
                  <Suggestions
                    searchSuggestion={searchSuggestion}
                    onSearchTrackEvent={onSearchTrackEvent}
                    onClose={closeSearchDrawer}
                    onNavigate={onCategoryOrSearchNavigate}
                  />
                  {recentSearch?.length > 0 && (
                    <OldRecentSearch
                      recentSearch={recentSearch}
                      setRecentSearch={setRecentSearch}
                      onSearchTrackEvent={onSearchTrackEvent}
                      onNavigate={onCategoryOrSearchNavigate}
                    />
                  )}
                </>
              )}
            </>
          )}
          <Drawer
            position="bottom"
            isOpen={isShowVoiceSearchDrawer}
            onDrawerClose={onCloseVoiceSearch}
            styles="max-h-[90vh]"
          >
            <VoiceSearch onSearchSubmit={onSubmitForm} setIsShowVoiceSearchDrawer={setIsShowVoiceSearchDrawer} />
          </Drawer>
        </div>
      )}
    </>
  );
};

MobileSearch.propTypes = {
  setShowSearchPopUp: PropTypes.func,
  catalogId: PropTypes.string,
  isSearchIconVisible: PropTypes.bool,
  searchBarStyles: PropTypes.object,
  inputClasses: PropTypes.string,
  isGiftFinderEnabled: PropTypes.bool,
};

export default MobileSearch;
